import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import "./style.scss";
import Select from 'react-select';
import { format, isSameDay } from 'date-fns';
import { RangesForSelect } from '../../utils/date-ranges';
import { has, isEmpty } from 'lodash';

/**
 * A reusable date range picker component that supports single, multiple, and range selection modes
 * 
 * @typedef {Object} ReportDateRangeProps
 * @property {Date|String} [startDate] - Initial start date for range mode or single date for single mode
 * @property {Date|String} [endDate] - Initial end date for range mode
 * @property {Function} [handleSelect] - Callback when a predefined range is selected (range mode only)
 * @property {Function} handleDateRange - Callback when dates are selected:
 *   - Range mode: (startDate: Date, endDate: Date) => void
 *   - Single mode: (date: Date) => void
 *   - Multiple mode: (dates: Date[]) => void
 *   - Month Days mode: (dates: number[]) => void
 * @property {Object} [startUpRange] - Optional initial predefined range to select (range mode only)
 * @property {Boolean} [displayLabel=false] - Whether to show the "Filter By Date" label
 * @property {('range'|'single'|'multiple')} [mode='range'] - Mode of date selection
 * @property {(1|2)} [monthsShown=2] - Number of months to show in calendar
 * @property {Boolean} [showPresetRanges=true] - Whether to show the preset range dropdown (range mode only)
 * @property {Date[]} [selectedDates=[]] - Array of selected dates (multiple mode only)
 * @property {Boolean} [todayButton=true] - Whether to show the today button
 * @property {Boolean} [lastDayOfMonth=false] - Whether to show the last day of month option (monthDays mode only)
 * @property {Function} [changeLastDayOfMonth] - Callback when the last day of month option is changed (monthDays mode only)
 * @property {String} [placeholder] - Placeholder text for the date picker
 * @property {Object} [style] - Custom styles for component elements
 *   @property {Object} [style.input] - Styles for the input element
 *   @property {Object} [style.container] - Styles for the input container
 *   @property {Object} [style.range_container] - Styles for the preset ranges container
 *   @property {Object} [style.range_control] - Styles for the preset ranges select control
 *   @property {Object} [style.range_option] - Styles for the preset ranges select options
 *   @property {Object} [style.range_group_heading] - Styles for the preset ranges group headings
 *   @property {Object} [style.single_value] - Styles for the preset ranges single value
 */
function ReportDateRange({
    startDate, endDate,
    handleSelect = () => { },
    handleDateRange = () => { },
    startUpRange, displayLabel = false,
    mode = 'range', monthsShown = 2,
    showPresetRanges = true, selectedDates = [],
    todayButton = true, lastDayOfMonth = false,
    changeLastDayOfMonth = () => { },
    placeholder, style = {}
}) {
    const [internalStartDate, setStartDate] = useState(startDate ? Date.parse(new Date(startDate)) : undefined);
    const [internalEndDate, setEndDate] = useState(endDate ? Date.parse(new Date(endDate)) : undefined);
    const [internalSelectedDates, setSelectedDates] = useState(selectedDates);
    const [selectedRange, setSelectedRange] = useState({});
    const [isCalendarOpen, setCalendarOpen] = useState(false);
    const [selectedMonthDays, setSelectedMonthDays] = useState(selectedDates);
    const monthDaysRef = useRef(null);

    // Default styles for the date picker input
    const defaultStyle = {
        input: {
            height: '38px',
            width: '200px',
            fontSize: '14px',
            border: '1px solid #ccc',
            color: 'hsla(0, 0%, 0%, 0.87)'
        },
        container: {
            padding: '0px',
            margin: '0px',
        },
        range_picker_container: {
            color: '#6f88a7',
            width: '250px',
            padding: '5px',
        },
        range_control: {
            width: '100%',
            fontSize: '14px',
            height: '38px',
            borderColor: '#ccc',
            boxShadow: '0 0 0 1px #ccc',
        },
        range_option: {
            padding: '3px 18px',
            fontSize: '14px',
        },
        range_group_heading: {
            fontSize: '10px',
            color: '#6f88a7',
            fontWeight: 'bold',
            backgroundColor: '#f8f9fa',
            padding: '4px 12px'
        },
        single_value: {
            color: 'hsla(0, 0%, 0%, 0.87)',
            boxShadow: 'none !important',
        }
    };

    // Set the selected range and dates when mode is range
    useEffect(() => {
        if (mode === 'range') {
            let hasRange = startUpRange?.from && startUpRange?.to;
            let range = hasRange ? startUpRange : RangesForSelect[1].options[2];
            if (!internalEndDate && !internalStartDate) {
                setSelectedRange(range);
                setStartDate(range.from);
                setEndDate(range.to);
                handleDateRange(new Date(range.from), new Date(range.to));
            };
            if (!hasRange && internalStartDate && internalEndDate) {
                showPresetRanges = false;
                handleDateRange(new Date(internalStartDate), new Date(internalEndDate));
            };
        };
    }, [startUpRange, mode]);

    // Close the month days dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (monthDaysRef.current && !monthDaysRef.current.contains(event.target)) {
                setCalendarOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    /**
     * On change select range handler
     * Sets the selected range in local state
     * Sends the selected range to parent component via handleSelect prop
     * If selected range is not custom:
     * - Updates internal start/end dates
     * - Sends new date range to parent via handleDateRange prop
     */
    function onChangeSelect(selected) {
        setSelectedRange(selected);
        handleSelect(selected);

        if (selected.group === 'Custom') {
            setCalendarOpen(true);
        } else {
            setCalendarOpen(false);
            setStartDate(selected.from);
            setEndDate(selected.to);
            handleDateRange(new Date(selected.from), new Date(selected.to));
        }

    }

    function handleDateChange(date) {
        switch (mode) {
            case 'range':
                const [start, end] = Array.isArray(date) ? date : [date, null];
                handleRangeDateChange(start, end);
                break;
            case 'single':
                handleSingleDateChange(date);
                break;
            case 'multiple':
                handleMultipleDateChange(date);
                break;
        }
    }

    /**
     * On change date range handler
     * Sets the start and end dates in local state
     * If the selected dates match a predefined range, updates the selected range
     * If the selected dates do not match a predefined range, updates the selected range to Custom
     * Sends the new date range to parent component via handleDateRange prop
     * Closes the calendar popup when both dates are selected
     * @private @function handleRangeDateChange
     * @param {Date} start - Start date
     * @param {Date} end - End date
     */
    function handleRangeDateChange(start, end) {
        let range = findRangeByStartEndDate(start, end);
        if (range) {
            let [groupIndex, optionIndex] = range;
            setSelectedRange(RangesForSelect[groupIndex].options[optionIndex]);
        } else {
            setSelectedRange(RangesForSelect[0].options[0]);
        }
        setStartDate(start);
        setEndDate(end);
        handleDateRange(new Date(start), new Date(end));

        if (start && end) {
            setCalendarOpen(false);
        }
    }

    function handleSingleDateChange(date) {
        setStartDate(date);
        setEndDate(date);
        handleDateRange(new Date(date));
        setCalendarOpen(false);
    }

    function handleMultipleDateChange(date) {
        const newDates = [...internalSelectedDates];
        const dateIndex = newDates.findIndex(d => d && date && isSameDay(d, date)
        );

        if (dateIndex >= 0) {
            newDates.splice(dateIndex, 1);
        } else {
            newDates.push(date);
        }
        setSelectedDates(newDates);
        handleDateRange(newDates);
    }

    function handleMonthDaysChange(day) {
        const newDates = [...selectedMonthDays];
        const dayIndex = newDates.indexOf(day);

        if (dayIndex >= 0) {
            newDates.splice(dayIndex, 1);
        } else {
            newDates.push(day);
        }
        setSelectedMonthDays(newDates);
        handleDateRange(newDates);
    }

    /**
     * Find a predefined range that matches given start and end dates
     * Searches through RangesForSelect array to find a matching range
     * @param {Date} start - Start date
     * @param {Date} end - End date
     * @returns {Array|null} Array containing group and option || null
     */
    function findRangeByStartEndDate(start, end) {
        let groupIndex, optionIndex;
        groupIndex = RangesForSelect.findIndex(group => {
            optionIndex = group.options.findIndex(range => {
                return isSameDay(range.from, new Date(start)) && isSameDay(range.to, new Date(end));
            });
            return optionIndex > -1;
        });
        return groupIndex > -1 ? [groupIndex, optionIndex] : null;
    }

    const formatSelectedDays = (days) => {
        if (!days.length) return '';
        return [...days].sort((a, b) => a - b).join(', ');
    };

    const monthDaysGrid = () => {
        const days = Array.from({ length: 31 }, (_, i) => i + 1);
        return (
            <div className="date-picker-container">
                <div className={`date-range d-flex position-right`}>
                    <div className="month-days-dropdown" ref={monthDaysRef}>
                        <input
                            readOnly
                            value={formatSelectedDays(selectedMonthDays)}
                            onClick={() => setCalendarOpen(!isCalendarOpen)}
                            placeholder={placeholder ? placeholder : "Select days..."}
                            className="month-days-input" />
                        {isCalendarOpen && (
                            <div className="month-days-container">
                                <div className="month-days-grid">
                                    {days.map(day => (
                                        <button
                                            key={day}
                                            className={`day-button ${selectedMonthDays.includes(day) ? 'selected' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMonthDaysChange(day);
                                            }}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                                <div className="lastDateMonth">
                                    <input
                                        className="isLastMonth"
                                        type="checkbox"
                                        checked={lastDayOfMonth}
                                        onChange={(e) => changeLastDayOfMonth(e.target.checked)} />
                                    <span>Last day of the month</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {displayLabel && <label>Filter By Date </label>}
            {showPresetRanges && mode === 'range' && (
                <div className="date-picker-container" style={{ ...defaultStyle.range_picker_container, ...style.range_picker_container }}>
                    <Select
                        //  menuIsOpen={true}
                        onChange={onChangeSelect}
                        options={RangesForSelect}
                        value={selectedRange}
                        maxMenuHeight={400}
                        isOptionSelected={(option, selectValue) => {
                            option.label === selectValue?.label && option.group === selectValue?.group;
                        }}
                        styles={{
                            control: (provided, state) => ({
                                ...provided,
                                ...defaultStyle.range_control,
                                ...style.range_control,
                                borderColor: state.isFocused ? '#47E5EB' : '#ccc',
                                boxShadow: state.isFocused ? '0 0 0 1px #47E5EB' : null,
                                '&:hover': {
                                    borderColor: state.isFocused ? '#47E5EB' : '#ccc'
                                },
                            }),
                            singleValue: (provided) => ({
                                ...provided,
                                ...defaultStyle.single_value,
                                ...style.single_value,
                            }),
                            option: (provided, state) => ({
                                ...provided,
                                ...defaultStyle.range_option,
                                ...style.range_option,
                                backgroundColor: state.isSelected ? '#47E5EB' : 'white',
                                color: state.isSelected ? 'white' : 'black',
                                '&:hover': {
                                    backgroundColor: state.isSelected ? '#47E5EB' : '#46e6ec33'
                                },
                            }),
                            groupHeading: (provided) => ({
                                ...provided,
                                ...defaultStyle.range_group_heading,
                                ...style.range_group_heading,
                            })
                        }}
                        defaultValue={selectedRange}
                    ></Select>
                </div>
            )}
            {mode === 'monthDays' ? monthDaysGrid() : (
                <div className="date-picker-container" style={{ ...defaultStyle.container, ...style.container }}>
                    <div className={`date-range ${monthsShown === 2 ? 'two-months' : ''} d-flex `}>
                        <DatePicker
                            key={internalSelectedDates?.length} // Rerenders so the styling is updated
                            placeholderText={placeholder ? placeholder : undefined}
                            monthsShown={monthsShown}
                            todayButton={todayButton ? "Today" : undefined}
                            onChange={handleDateChange}
                            selected={mode === 'single' ? internalStartDate :
                                mode === 'multiple' ? null :
                                    mode === 'range' ? internalStartDate :
                                        null}
                            //range mode
                            startDate={mode === 'range' ? internalStartDate : undefined}
                            endDate={mode === 'range' ? internalEndDate : undefined}
                            selectsRange={mode === 'range'}
                            //multiple mode
                            multiple={mode === 'multiple'}
                            dayClassName={date => {
                                if (mode === 'multiple' &&
                                    internalSelectedDates.some(d => isSameDay(d, date))) {
                                    return 'custom-selected-day';
                                }
                                return undefined;
                            }}
                            open={isCalendarOpen}
                            onClickOutside={() => setCalendarOpen(false)}
                            onInputClick={() => setCalendarOpen(true)}
                            customInput={<input
                                style={{
                                    ...defaultStyle.input,
                                    ...style.input
                                }} />} />
                    </div>
                </div>
            )}
        </>
    );
}

export default ReportDateRange;

